import type { DplxAdSet } from '../../server/api/v2/dplx-api/ads-feed/models'
import { settledConsentDecision } from '../../utils/consent/consentGate'
import { OpenFeature } from '@openfeature/web-sdk'
import type { AdProviderType, ApiAdProvider } from './types'

export interface DplxApiAd {
  id: string
  title: string
  description?: string
  imageUrl: string
  clickUrl: string
  brand: string
  creativeId?: string
  clickTracker: string[]
  impressionTracker: string
  viewableTracker: string[]
  pixelTracker?: string[]
}

export interface DplxAdEventData extends DplxApiAd {
  placement: string
  tilePosition: number
}

export class DplxapiProvider implements ApiAdProvider {
  readonly name: AdProviderType = 'dplxapi'
  private baseUrl: string = '/api/v2/dplx-api/ads-feed'

  // Since autolayout tile position isn't working (always returning -1) we have ad counter
  // to track for API calls
  public adIndex: number = 0
  private pageToken: string | null = null
  private fetchQueue: Promise<void> = Promise.resolve()

  resetIndex(): void {
    this.adIndex = 0
    this.pageToken = null
    this.fetchQueue = Promise.resolve()
  }

  async fetchAd(_options: Partial<DplxAdEventData>): Promise<DplxApiAd | null> {
    if (!import.meta.client) return null

    const run = this.fetchQueue.then(async () => {
      const consent = await settledConsentDecision()
      if (consent.state !== 'allow') {
        return []
      }
      return this.requestAdsFromApi(
        1,
        this.adIndex++,
        consent.mayResolveIdentity
      )
    })
    this.fetchQueue = run.then(
      () => undefined,
      () => undefined
    )

    try {
      const ads = await run
      if (ads.length === 0) {
        return null
      }

      return ads[0]
    } catch (error) {
      console.error('[DplxAPI] Failed to fetch ad:', error)
      return null
    }
  }

  async fetchAdsFromApi(
    count: number = 1,
    index: number = 0
  ): Promise<DplxApiAd[]> {
    const consent = await settledConsentDecision()
    if (consent.state !== 'allow') {
      return []
    }

    return this.requestAdsFromApi(count, index, consent.mayResolveIdentity)
  }

  private async requestAdsFromApi(
    count: number,
    index: number,
    mayResolveIdentity: boolean
  ): Promise<DplxApiAd[]> {
    try {
      const params = new URLSearchParams({
        maxNumAds: String(Math.min(count, 5)),
        // use providers index instead of autolayout tile position,
        // also increase counter by 1
        idx: String(index || 0),
      })
      if (mayResolveIdentity) {
        const { targetingKey } = OpenFeature.getContext()
        if (typeof targetingKey === 'string' && targetingKey) {
          params.set('customTrackingId', targetingKey)
        }
      }

      // Resend the t token from the previous request on this page. The first
      // request (idx=0) is skipped
      if (this.pageToken) {
        params.set('t', this.pageToken)
      }

      const response = await fetch(`${this.baseUrl}?${params}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      })

      if (!response.ok) {
        throw new Error(
          `DplxAPI request failed: ${response.status} ${response.statusText}`
        )
      }

      const data = await response.json()

      if (typeof data?.t === 'string') {
        this.pageToken = data.t
      }

      const adSets: DplxAdSet[] = data?.result?.adSets || []

      const processedAds = adSets.map(
        (adSet, index): DplxApiAd => ({
          id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID per request
          title: adSet.adSetTitle,
          description: adSet.adSetDesc ?? '',
          imageUrl: adSet.adSetMediaURL,
          clickUrl: adSet.clickURL,
          brand: adSet.adAttribution,
          clickTracker: adSet.clickTracker || [],
          impressionTracker: adSet.impressionTracker,
          viewableTracker: adSet.viewableTracker || [],
          pixelTracker: adSet.pixelTracker || [],
        })
      )

      return processedAds
    } catch (error) {
      console.error('[DplxAPI] Failed to fetch ads from API:', error)
      return []
    }
  }

  trackImpression(ad: DplxApiAd): void {
    if (ad.impressionTracker) {
      fetch(ad.impressionTracker, { method: 'GET', mode: 'no-cors' }).catch(
        (error) => console.error('[DplxAPI] Failed to track impression:', error)
      )
    }
  }
  trackViewable(ad: DplxApiAd): void {
    for (const url of ad.viewableTracker) {
      fetch(url, { method: 'GET', mode: 'no-cors' }).catch((error) =>
        console.error('[DplxAPI] Failed to track viewable:', error)
      )
    }
  }

  trackPixel(ad: DplxApiAd): void {
    if (!ad.pixelTracker) return
    for (const url of ad.pixelTracker) {
      fetch(url, { method: 'GET', mode: 'no-cors' }).catch((error) =>
        console.error('[DplxAPI] Failed to track pixel:', error)
      )
    }
  }

  handleAdClick(ad: DplxApiAd): void {
    for (const url of ad.clickTracker) {
      fetch(url, { method: 'GET', mode: 'no-cors' }).catch((error) =>
        console.error('[DplxAPI] Failed to track click:', error)
      )
    }
  }

  getEventData(data?: DplxAdEventData): NtpAdEvent {
    return {
      ntp_ad_provider: 'dplxapi',
      ntp_ad_brand: data?.brand || '',
      ntp_ad_clickurl: data?.clickUrl || '',
      ntp_placement_name: data?.placement || '',
      ntp_creative_id: data?.creativeId || '',
      ntp_tile_position: data?.tilePosition || undefined,
    }
  }
}

export const dplxapiProvider = new DplxapiProvider()
